import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import Loader from "../../components/Loader";
import { formatString, pipe } from "../../resources/helpers";
import useI18n from "../../state/i18n";
import useProducts from "../../state/products";
import {
  SLIDE_IN_LEFT,
  SLIDE_OUT_DOWN, WithTransition
} from "../../styles/transitions/transitions";
import { ProductsByCategories } from "./containers/ProductsByCategories";
import Icon from "@etiquette-ui/icons";
import { Input } from "@etiquette-ui/inputs-dropdowns";

const Container = styled.div`
  top: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  border-radius: 4px;
  width: 100%;
  padding-top: 15px;
  padding-bottom: 8px;
  background-color: #fff;
  z-index: 300;
  max-width: 414px;
`;

const IconContainer = styled.div`
  height: 100%;
  display: flex;
  margin-Left: 11px;
  width: 30px;
  align-items: center;
  pointer-events: none;
  justify-content: center;
`;

const CloseContainer = styled.div`
  width: 30px;
  display: flex;
  margin-right: 11px;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  padding-top: 2.6rem;
  background-color: white;
`;

function getProductsWithCategories(data) {
  return data.map((category) => ({
    ...category,
    products: [
      ...category.products.map((product) => ({
        ...product,
        translations: product.translations,
      })),
      ...category.subcategories
        .reduce((acc, curr) => acc.concat([...curr.products]), [])
        .map((product) => ({
          ...product,
          translations: product.translations,
        })),
    ],
  }));
}

const Search = () => {
  const [search, setSearch] = useState("");
  const [productsFiltered, setProductsFiltered] = useState([]);
  const [products] = useProducts();
  const [i18n] = useI18n();

  const [data] = useState(pipe(getProductsWithCategories)(products.tree));

  const history = useHistory();

  const { redirectTo = "/menu" } = useLocation();

  const handleClose = () => history.push(redirectTo);

  const handleSearch = (e) => setSearch(e.target.value);

  const inputRef = useRef(null);

  useEffect(()=>{
    if (inputRef.current) {
      if(!inputRef.current.autofocus){
        inputRef.current.focus();
      }
    }
  },[]);

  useEffect(() => {
    const query = search.toLowerCase().trim();

    const productsFiltered = data.map((category) => ({
        ...category,
        products: [
            ...category.products.filter(
                (product) =>
                    formatString(product.title).includes(query) ||
                    product.tags.some((tag) =>
                        formatString(tag.title).includes(query)
                    ) ||
                    product.ingredients.some((ing) =>
                        formatString(ing.title).includes(query)
                    )
            ),
            ...category.subcategories.filter(
              (sc) =>
                formatString(sc.title).includes(query)
            ).reduce((acc, curr)=>[...acc, ...curr.products], []),
            ...(formatString(category.title).includes(query) ? category.products : []),
        ],
    }));

    const removeDuplicates = productsFiltered.map((category) => {
      const products = [
        ...new Map(
          category.products.map((product) => [product.id, product])
        ).values(),
      ];

      return {
        ...category,
        products,
      };
    });

    setProductsFiltered(removeDuplicates);
  }, [search, data]);

  return (
    <WithTransition enter={SLIDE_IN_LEFT} exit={SLIDE_OUT_DOWN}>
      <Loader isLoading={data.length <= 0}>
        <Container>
          <IconContainer>
            <Icon
              icon={'search'}
              styles={{
                svg: {
                  width: '20px',
                  height: '20px',
                },
                line: {
                  fill: 'none',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeWidth: '3px',
                  stroke: 'black',
                },
              }}
            />
          </IconContainer>
          <Input
            inputProps={{
              placeholder: i18n.tag("SEARCH_PLACEHOLDER"),
              onChange: handleSearch,
              ref: inputRef
            }}
            style={{
              flexGrow: '1',
              '--bg': 'white',
            }}
            fontBaseSize='16px'
          />
          <CloseContainer onClick={handleClose}>
            <Icon
              icon={'wrong'}
              styles={{
                svg: {
                  width: '22px',
                  height: '22px',
                },
                line: {
                  fill: 'none',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeWidth: '2px',
                  stroke: 'black',
                },
              }}
            />
          </CloseContainer>
        </Container>
        <Wrapper>
          <ProductsByCategories categories={productsFiltered} />
        </Wrapper>
      </Loader>
    </WithTransition>
  );
};

export default Search;
